import { useAnalytics } from '../context/AnalyticsContext';

const PALETTE = [
	'#059669','#2563EB','#D97706','#7C3AED','#DC2626',
	'#0891B2','#65A30D','#C2410C','#9333EA','#0369A1',
	'#BE185D','#0F766E','#B45309','#4338CA','#B91C1C',
	'#0369A1','#15803D','#C2410C','#6D28D9','#0E7490',
];

function getInitials(name) {
	if (!name) return '?';
	return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
}

export default function TopUsersChart() {
	const { state, selectUser } = useAnalytics();
	const { topUsers, filteredUsers, selectedModule, selectedUser, selectedRole, loading, filterLoading, filterType } = state;

	// Show filtered data whenever ANY filter is active
	const anyFilter    = !!filterType;
	// Users chart is clickable unless a MODULE filter is the drill-down
	// (when module is selected we show who accessed it — not clickable)
	const isModuleDrill = filterType === 'module';
	const isUserSel     = filterType === 'user' && !!selectedUser;

	const data = anyFilter ? filteredUsers : topUsers;
	const busy = anyFilter ? filterLoading  : loading;
	const max  = Math.max(...(data?.map(u => u.views) ?? [1]), 1);

	if (busy) return <div style={S.empty}>Loading…</div>;
	if (!data?.length) return (
		<div style={S.empty}>
			{filterType === 'module' ? `No users accessed "${selectedModule?.label}"`
			: filterType === 'role'  ? `No users for role "${selectedRole?.label}"`
			: 'No data for the selected period'}
		</div>
	);

	return (
		<div style={S.scrollWrap}>
			{data.map((user, i) => {
				const pct        = Math.round((user.views / max) * 100);
				const col        = PALETTE[i % PALETTE.length];
				const initials   = getInitials(user.label);
				const isSelected = isUserSel && selectedUser?.id === user.id;
				const isOther    = isUserSel && !isSelected;
				const clickable  = !isModuleDrill;

				return (
					<div
						key={user.id ?? i}
						onClick={() => clickable && selectUser({ id: user.id, label: user.label })}
						style={{ ...S.row, opacity: isOther ? 0.35 : 1, cursor: clickable ? 'pointer' : 'default' }}
						title={clickable ? (isSelected ? 'Click to clear filter' : `Filter by ${user.label}`) : undefined}
					>
						<div style={S.labelRow}>
							<div style={S.nameWrap}>
								<div style={{
									...S.avatar,
									background: isSelected ? col : col + '18',
									color: isSelected ? '#fff' : col,
									boxShadow: isSelected ? `0 0 0 2px ${col}` : 'none',
								}}>
									{initials}
								</div>
								<span style={{ ...S.name, fontWeight: isSelected ? 700 : 400 }} title={user.label}>
									{user.label ?? '—'}
								</span>
								{isSelected && <span style={S.tag}>filtered</span>}
							</div>
							<span style={{ ...S.count, color: col }}>{(user.views ?? 0).toLocaleString('en-GB')}</span>
						</div>
						<div style={S.track}>
							<div style={{ ...S.fill, width: `${pct}%`, background: col }} />
						</div>
					</div>
				);
			})}
		</div>
	);
}

const S = {
	scrollWrap: { overflowY: 'auto', maxHeight: 360, display: 'flex', flexDirection: 'column', gap: 11, paddingRight: 4 },
	row:        { transition: 'opacity .2s', flexShrink: 0 },
	labelRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
	nameWrap:   { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 },
	avatar: {
		width: 26, height: 26, borderRadius: 8, flexShrink: 0,
		display: 'flex', alignItems: 'center', justifyContent: 'center',
		fontSize: 10, fontWeight: 700, transition: 'all .2s',
	},
	name:  { fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
	tag:   { fontSize: 10, fontWeight: 700, color: '#059669', background: '#ECFDF5', padding: '1px 6px', borderRadius: 10, flexShrink: 0 },
	count: { fontSize: 13, fontWeight: 700, flexShrink: 0, marginLeft: 8 },
	track: { height: 6, background: '#F1F5F9', borderRadius: 99, overflow: 'hidden' },
	fill:  { height: '100%', borderRadius: 99, transition: 'width .8s cubic-bezier(.4,0,.2,1)' },
	empty: { color: '#94A3B8', textAlign: 'center', padding: '32px 0', fontSize: 13 },
};